import * as Yup from "yup";

import { totalCostLabelText } from "@krea/lender-features/components/total-cost-label/constants";

export const TotalCostValidation = (
  formatWithCurrency: (value: number) => string,
) =>
  Yup.number()
    .typeError(`"${totalCostLabelText}" is required`)
    .required(`"${totalCostLabelText}" is required`)
    .positive("Should be a positive number")
    .min(
      1000,
      (s) => `Total cost can't be lower than ${formatWithCurrency(s.min)}`,
    )
    .max(
      1000 * 1000 * 1000,
      (schema) =>
        `"${totalCostLabelText}" can't be greater than ${formatWithCurrency(schema.max)}`,
    )
    .when(
      ["amount", "setupFee", "administrationFees", "paybackTimeMonths"],
      ([amount, setupFee, administrationFees, paybackTimeMonths], schema) => {
        if (!amount) {
          return schema;
        }

        const feesOverTerm =
          ((administrationFees as number) || 0) *
          ((paybackTimeMonths as number) || 0);
        const minimum =
          (amount as number) + ((setupFee as number) || 0) + feesOverTerm;

        // The indicative repayment plan carves the administration fees out of
        // total cost, so a total that cannot cover them produces NO plan at all
        // rather than an error (i_total < 0 -> the backend returns null and the
        // section silently unmounts). This bound is what keeps that unreachable.
        if (feesOverTerm > 0) {
          return schema.min(
            minimum,
            `All fees should be included in the total cost — it must be at least ${formatWithCurrency(minimum)} (approved amount + setup fee + ${paybackTimeMonths} months of administration fees).`,
          );
        }

        if (setupFee) {
          return schema.moreThan(
            minimum - 1,
            "Setup fee should be included in calculation of total cost. Currently total cost is smaller than amount + setup fee.",
          );
        }

        return schema.min(
          amount as number,
          (s) =>
            `"${totalCostLabelText}" can't be lower than approved amount (${formatWithCurrency(s.min)})`,
        );
      },
    );
